import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '@massilia/ui';
import { Calendar } from '@massilia/ui';
import { OptionalLabel } from '@massilia/ui';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@massilia/ui';
import { Popover, PopoverContent, PopoverTrigger } from '@massilia/ui';
import { useStore } from '@tanstack/react-form';
import { addYears, format, isValid, parse } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import React from 'react';
import { useFieldContext } from '../hooks/use-app-form.js';
import { toDateOnlyString } from '../utils/date-only.js';
import { FieldDisplayValue } from './field-display-value';
import { FieldError } from './field-error';
const MASK = '__/__/____';
function formatDigitsToMask(digits) {
    const d = digits.replace(/\D/g, '').slice(0, 8);
    const padded = d + '_'.repeat(8 - d.length);
    return `${padded.slice(0, 2)}/${padded.slice(2, 4)}/${padded.slice(4, 8)}`;
}
function getCursorPositionForDigitCount(digitCount) {
    if (digitCount <= 2)
        return digitCount;
    if (digitCount <= 4)
        return digitCount + 1;
    return Math.min(digitCount + 2, 10);
}
// eslint-disable-next-line perfectionist/sort-modules
export default function DateField({ autoFocus, label, onDisplayClick, optional = false, renderType = 'edit', }) {
    const field = useFieldContext();
    const errors = useStore(field.store, (state) => state.meta.errors);
    const inputRef = React.useRef(null);
    const [open, setOpen] = React.useState(false);
    // Derive values from field state
    const fieldDate = field.state.value ? new Date(field.state.value) : undefined;
    const displayValue = field.state.value ? format(new Date(field.state.value), 'dd/MM/yyyy') : MASK;
    const [inputValue, setInputValue] = React.useState(displayValue);
    React.useEffect(() => {
        setInputValue(displayValue);
    }, [displayValue]);
    const tryParseAndSetDate = React.useCallback((digits) => {
        if (digits.length === 8) {
            const date = parse(digits, 'ddMMyyyy', new Date());
            if (isValid(date)) {
                field.handleChange(toDateOnlyString(date));
            }
        }
        else if (digits.length === 0 && optional) {
            field.handleChange(null);
        }
    }, [field, optional]);
    if (renderType === 'display') {
        const formattedDate = field.state.value ? format(new Date(field.state.value), 'dd/MM/yyyy') : '';
        return _jsx(FieldDisplayValue, { label: label, onClick: onDisplayClick, value: formattedDate });
    }
    return (_jsxs("div", { className: "flex flex-1 flex-col gap-2", children: [_jsx(OptionalLabel, { "aria-invalid": errors.length > 0, label: label, optional: optional }), _jsxs(InputGroup, { children: [_jsxs(Popover, { modal: true, onOpenChange: setOpen, open: open, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsx(InputGroupAddon, { children: _jsxs(InputGroupButton, { id: "date-picker", size: "icon-xs", variant: "ghost", children: [_jsx(CalendarIcon, { className: cn('text-primary', { 'text-destructive': errors.length > 0 }) }), _jsx("span", { className: "sr-only", children: "Select date" })] }) }) }), _jsx(PopoverContent, { align: "end", alignOffset: -8, className: "w-auto overflow-hidden p-0", sideOffset: 10, children: _jsx(Calendar, { captionLayout: "dropdown", defaultMonth: fieldDate, endMonth: addYears(new Date(), 5), mode: "single", onSelect: (date) => {
                                        setOpen(false);
                                        if (!date)
                                            return;
                                        field.handleChange(toDateOnlyString(date));
                                    }, selected: fieldDate }) })] }), _jsx(InputGroupInput, { ref: inputRef, autoFocus: autoFocus, "aria-invalid": errors.length > 0, id: "date", onBlur: field.handleBlur, onChange: (e) => {
                            const input = e.target;
                            const oldDigits = inputValue.replace(/\D/g, '').replace(/_/g, '');
                            const newRawValue = e.target.value;
                            const newDigits = newRawValue.replace(/\D/g, '').replace(/_/g, '').slice(0, 8);
                            if (newDigits !== oldDigits) {
                                const masked = formatDigitsToMask(newDigits);
                                setInputValue(masked);
                                tryParseAndSetDate(newDigits);
                                const newCursor = getCursorPositionForDigitCount(newDigits.length);
                                requestAnimationFrame(() => {
                                    input.setSelectionRange(newCursor, newCursor);
                                });
                            }
                        }, onClick: (e) => {
                            const input = e.currentTarget;
                            const digits = inputValue.replace(/\D/g, '').replace(/_/g, '');
                            const cursorPos = getCursorPositionForDigitCount(digits.length);
                            input.setSelectionRange(cursorPos, cursorPos);
                        }, onFocus: (e) => {
                            const input = e.currentTarget;
                            const digits = inputValue.replace(/\D/g, '').replace(/_/g, '');
                            const cursorPos = getCursorPositionForDigitCount(digits.length);
                            requestAnimationFrame(() => {
                                input.setSelectionRange(cursorPos, cursorPos);
                            });
                        }, onKeyDown: (e) => {
                            const input = e.currentTarget;
                            if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                setOpen(true);
                                return;
                            }
                            if (e.key === 'Backspace') {
                                e.preventDefault();
                                const digits = inputValue.replace(/\D/g, '').replace(/_/g, '');
                                if (digits.length > 0) {
                                    const newDigits = digits.slice(0, -1);
                                    const masked = formatDigitsToMask(newDigits);
                                    setInputValue(masked);
                                    tryParseAndSetDate(newDigits);
                                    const newCursor = getCursorPositionForDigitCount(newDigits.length);
                                    requestAnimationFrame(() => {
                                        input.setSelectionRange(newCursor, newCursor);
                                    });
                                }
                                return;
                            }
                            // Block Delete key - no effect in typewriter mode
                            if (e.key === 'Delete') {
                                e.preventDefault();
                                return;
                            }
                            // Block arrow keys - cursor is locked
                            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                                e.preventDefault();
                                return;
                            }
                            // Block non-digit keys (allow Tab, etc.)
                            if (e.key.length === 1 && !/\d/.test(e.key)) {
                                e.preventDefault();
                            }
                        }, value: inputValue })] }), _jsx(FieldError, { errors: errors })] }));
}
